import React, { useEffect, useMemo, useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Select } from '../../../components/ui/Select';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/Button';
import { Settings, Shield, Bell, Database, Info, Check, Sliders, RotateCcw } from 'lucide-react';
import { dataLayer } from '../../../data/dataLayer';
import { V11_CONFIG_FIELDS, type V11ConfigField, type V11ConfigOverrides, getV11ConfigOverrides, loadV11ConfigOverrides, saveV11ConfigOverrides, resetV11ConfigOverrides } from '../../../mastery/selection/v11ConfigStore';
import { getSnapshotCap, setSnapshotCap, DEFAULT_SNAPSHOT_CAP } from '../../../governance/pipeline/snapshots/snapshotStorage';
interface SettingToggleProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}
function SettingToggle({
  label,
  description,
  value,
  onChange
}: SettingToggleProps) {
  return <div className="flex items-center justify-between py-4 border-b border-gray-50 last:border-0" data-id="element-6300">
      <div className="flex-1 pr-4" data-id="element-6301">
        <div className="text-sm font-medium text-gray-900" data-id="element-6302">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-6303">{description}</div>
      </div>
      <Toggle checked={value} onChange={onChange} label={label} variant="admin" data-id="element-6304" />
    </div>;
}
interface SettingSelectProps {
  label: string;
  description: string;
  value: string;
  options: Array<{
    value: string;
    label: string;
  }>;
  onChange: (v: string) => void;
}
function SettingSelect({
  label,
  description,
  value,
  options,
  onChange
}: SettingSelectProps) {
  return <div className="flex flex-col sm:flex-row sm:items-center justify-between py-4 border-b border-gray-50 last:border-0 gap-3" data-id="element-6305">
      <div className="flex-1 pr-4" data-id="element-6306">
        <div className="text-sm font-medium text-gray-900" data-id="element-6307">{label}</div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-6308">{description}</div>
      </div>
      <Select options={options} value={value} onChange={e => onChange(e.target.value)} fullWidth={false} size="sm" className="w-full sm:w-auto" data-id="element-6309" />
    </div>;
}
const sections = [{
  id: 'general',
  label: 'Algemeen',
  icon: <Settings className="w-4 h-4" data-id="element-6310" />
}, {
  id: 'governance',
  label: 'Governance',
  icon: <Shield className="w-4 h-4" data-id="element-6311" />
}, {
  id: 'adaptive',
  label: 'Adaptive Engine',
  icon: <Sliders className="w-4 h-4" data-id="element-6312" />
}, {
  id: 'notifications',
  label: 'Meldingen',
  icon: <Bell className="w-4 h-4" data-id="element-6313" />
}, {
  id: 'data',
  label: 'Data & Opslag',
  icon: <Database className="w-4 h-4" data-id="element-6314" />
}] as const;
type SectionId = (typeof sections)[number]['id'];
const DEFAULT_ADMIN_SETTINGS = {
  strictMode: false,
  debugLogging: false,
  showConnectionBadges: true,
  language: 'nl',
  autoValidate: true,
  blockOnError: false,
  warnOnWarning: true,
  governanceLevel: 'standard',
  notifyOnError: true,
  notifyOnWarning: false,
  notifyOnNewAccount: true,
  persistSessions: true,
  maxSessionHistory: '1000',
  autoCleanup: false
};
export function SettingsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>('general');
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    ...DEFAULT_ADMIN_SETTINGS
  });
  // Load persisted settings from dataLayer on mount
  useEffect(() => {
    dataLayer.getAdminSettings().then(persisted => {
      if (persisted) {
        setSettings(prev => ({
          ...prev,
          ...(persisted as Partial<typeof DEFAULT_ADMIN_SETTINGS>)
        }));
      }
    }).catch(() => {});
  }, []);
  const update = (key: keyof typeof settings, value: boolean | string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleSave = () => {
    dataLayer.saveAdminSettings(settings as Record<string, unknown>);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return <PageLayout title="Instellingen" subtitle="Systeem- en governance-instellingen" breadcrumbs={[{
    label: 'Systeem'
  }, {
    label: 'Instellingen'
  }]} actions={<div className="flex items-center gap-2" data-id="element-6316">
          <ConnectionBadge status="live" data-id="element-6317" />
          <Button onClick={handleSave} variant={saved ? 'success' : 'primary'} size="sm" className={saved ? '' : 'bg-blue-600 hover:bg-blue-700 shadow-none'} data-id="element-6318">
            {saved ? <>
                <Check className="w-4 h-4 mr-1.5" data-id="element-6319" /> Opgeslagen
              </> : 'Opslaan'}
          </Button>
        </div>} data-id="element-6315">
      <div className="flex flex-col md:flex-row gap-6" data-id="element-6320">
        {/* Section Nav */}
        <div className="w-full md:w-56 flex-shrink-0" data-id="element-6321">
          {/* Mobile: Horizontal scrollable tabs */}
          <div className="md:hidden" data-id="element-6322">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto hide-scrollbar" data-id="element-6323">
              {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex-shrink-0 flex items-center gap-2 px-3.5 py-2 rounded-md text-sm transition-colors whitespace-nowrap focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${activeSection === section.id ? 'bg-white text-gray-900 font-medium shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-6324">
                  {section.icon}
                  {section.label}
                </button>)}
            </div>
          </div>

          {/* Desktop: Vertical nav */}
          <nav className="hidden md:flex flex-col gap-1" data-id="element-6325">
            {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors font-medium focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none ${activeSection === section.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`} data-id="element-6326">
                {section.icon}
                {section.label}
              </button>)}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-6327">
          {activeSection === 'general' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-6328">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6329">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-6330">
                  Algemene instellingen
                </h2>
              </div>
              <div className="px-4 sm:px-5" data-id="element-6331">
                <SettingToggle label="Strict mode" description="Striktere validatie en foutafhandeling" value={settings.strictMode} onChange={v => update('strictMode', v)} data-id="element-6332" />
                <SettingToggle label="Debug logging" description="Uitgebreide logging in de browser console" value={settings.debugLogging} onChange={v => update('debugLogging', v)} data-id="element-6333" />
                <SettingToggle label="Verbindingsbadges tonen" description="Toon live/mock badges op elke pagina" value={settings.showConnectionBadges} onChange={v => update('showConnectionBadges', v)} data-id="element-6334" />
                <SettingSelect label="Taal" description="Taal van de admin interface" value={settings.language} options={[{
              value: 'nl',
              label: 'Nederlands'
            }, {
              value: 'en',
              label: 'English'
            }]} onChange={v => update('language', v)} data-id="element-6335" />
              </div>
            </div>}

          {activeSection === 'governance' && <div className="space-y-4" data-id="element-6336">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-6337">
                <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6338">
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-6339">
                    Governance pipeline
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-6340">
                    Instellingen voor de validatie- en governance-pipeline
                  </p>
                </div>
                <div className="px-4 sm:px-5" data-id="element-6341">
                  <SettingToggle label="Automatisch valideren" description="Valideer vragen automatisch bij het laden" value={settings.autoValidate} onChange={v => update('autoValidate', v)} data-id="element-6342" />
                  <SettingToggle label="Blokkeren bij fouten" description="Blokkeer het systeem bij governance-fouten" value={settings.blockOnError} onChange={v => update('blockOnError', v)} data-id="element-6343" />
                  <SettingToggle label="Waarschuwen bij warnings" description="Toon waarschuwingen bij governance-issues" value={settings.warnOnWarning} onChange={v => update('warnOnWarning', v)} data-id="element-6344" />
                  <SettingSelect label="Governance niveau" description="Strengheid van de governance-regels" value={settings.governanceLevel} options={[{
                value: 'lenient',
                label: 'Licht (alleen fouten)'
              }, {
                value: 'standard',
                label: 'Standaard (fouten + warnings)'
              }, {
                value: 'strict',
                label: 'Strikt (alles)'
              }]} onChange={v => update('governanceLevel', v)} data-id="element-6345" />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 sm:px-5 py-4 flex items-start gap-3" data-id="element-6346">
                <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-6347" />
                <div data-id="element-6348">
                  <p className="text-sm font-bold text-blue-900" data-id="element-6349">
                    Governance pipeline
                  </p>
                  <p className="text-xs sm:text-sm text-blue-800 mt-1 leading-relaxed" data-id="element-6350">
                    De governance pipeline controleert alle vragen op
                    schema-validatie, type-regels, inhoudskwaliteit en visuele
                    governance. Zie de <strong data-id="element-6351">Validatieregels</strong> pagina
                    voor een volledige compliance-check.
                  </p>
                </div>
              </div>
            </div>}

          {activeSection === 'adaptive' && <AdaptiveEngineSection data-id="element-6352" />}

          {activeSection === 'notifications' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-6353">
              <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6354">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-6355">
                  Meldingsinstellingen
                </h2>
              </div>
              <div className="px-4 sm:px-5" data-id="element-6356">
                <SettingToggle label="Melden bij fouten" description="Stuur een melding bij governance-fouten" value={settings.notifyOnError} onChange={v => update('notifyOnError', v)} data-id="element-6357" />
                <SettingToggle label="Melden bij waarschuwingen" description="Stuur een melding bij governance-waarschuwingen" value={settings.notifyOnWarning} onChange={v => update('notifyOnWarning', v)} data-id="element-6358" />
                <SettingToggle label="Melden bij nieuw account" description="Stuur een melding bij een nieuw account" value={settings.notifyOnNewAccount} onChange={v => update('notifyOnNewAccount', v)} data-id="element-6359" />
              </div>
            </div>}

          {activeSection === 'data' && <div className="space-y-4" data-id="element-6360">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-6361">
                <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6362">
                  <h2 className="text-sm font-semibold text-gray-900" data-id="element-6363">
                    Data & opslag
                  </h2>
                </div>
                <div className="px-4 sm:px-5" data-id="element-6364">
                  <SettingToggle label="Sessies bewaren" description="Bewaar oefensessies in lokale opslag" value={settings.persistSessions} onChange={v => update('persistSessions', v)} data-id="element-6365" />
                  <SettingToggle label="Automatisch opruimen" description="Verwijder oude sessies automatisch" value={settings.autoCleanup} onChange={v => update('autoCleanup', v)} data-id="element-6366" />
                  <SettingSelect label="Max. sessiegeschiedenis" description="Maximum aantal sessies om te bewaren" value={settings.maxSessionHistory} options={[{
                value: '100',
                label: '100 sessies'
              }, {
                value: '500',
                label: '500 sessies'
              }, {
                value: '1000',
                label: '1.000 sessies'
              }, {
                value: '5000',
                label: '5.000 sessies'
              }]} onChange={v => update('maxSessionHistory', v)} data-id="element-6367" />
                </div>
              </div>

              <SnapshotCapSection data-id="element-6368" />

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 sm:px-5 py-4 flex items-start gap-3" data-id="element-6369">
                <Info className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-6370" />
                <div data-id="element-6371">
                  <p className="text-sm font-bold text-amber-900" data-id="element-6372">
                    Lokale opslag
                  </p>
                  <p className="text-xs sm:text-sm text-amber-800 mt-1 leading-relaxed" data-id="element-6373">
                    Alle data wordt lokaal opgeslagen in de browser. Er is geen
                    externe database verbinding. Data gaat verloren bij het
                    wissen van de browser-cache.
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </PageLayout>;
}
// ─── Snapshot Cap Section ───────────────────────────────────────────────
function SnapshotCapSection() {
  const [cap, setCap] = useState<string>(String(DEFAULT_SNAPSHOT_CAP));
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setCap(String(getSnapshotCap()));
  }, []);
  const handleChange = (value: string) => {
    setCap(value);
    setSnapshotCap(Number(value));
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-6374">
      <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6375">
        <h2 className="text-sm font-semibold text-gray-900" data-id="element-6376">
          Governance snapshots
          {saved && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-id="element-6377">
              <Check className="w-3 h-3" data-id="element-6378" /> Opgeslagen
            </span>}
        </h2>
        <p className="text-xs text-gray-500 mt-0.5" data-id="element-6379">
          Hoeveel pipeline-snapshots in lokale opslag bewaard worden voor
          vergelijkingen. Oudste wordt eerst opgeruimd.
        </p>
      </div>
      <div className="px-4 sm:px-5" data-id="element-6380">
        <SettingSelect label="Max. snapshots" description={`Standaard ${DEFAULT_SNAPSHOT_CAP}. Hoger = meer historie, meer opslag.`} value={cap} options={[{
        value: '5',
        label: '5 snapshots'
      }, {
        value: '10',
        label: '10 snapshots'
      }, {
        value: '20',
        label: '20 snapshots (standaard)'
      }, {
        value: '50',
        label: '50 snapshots'
      }, {
        value: '100',
        label: '100 snapshots'
      }]} onChange={handleChange} data-id="element-6381" />
      </div>
    </div>;
}
// ─── Adaptive Engine Section (P-14) ──────────────────────────────────────
const GROUP_META: Record<V11ConfigField['group'], {
  label: string;
  description: string;
}> = {
  mastery: {
    label: 'Mastery drempels',
    description: 'Wanneer een family als struggling, learning of mastered geldt, en wanneer prerequisites/plus-tier ontgrendelen.'
  },
  session: {
    label: 'Sessie samenstelling',
    description: 'Hoeveel families per sessie, hoeveel mastered content, en hoe sterk recency telt.'
  },
  tier: {
    label: 'Tier window',
    description: 'Hoeveel tiers onder en boven target worden meegenomen in selectie en fallback.'
  },
  pedagogy: {
    label: 'Pedagogische bias',
    description: 'B/M/E proximity boost en frustratiedetectie voor mid-session swaps.'
  },
  diversity: {
    label: 'Variatie & interleaving',
    description: 'Voorkomt clustering van zelfde skill, cognitive level of vraagtype.'
  }
};
function AdaptiveEngineSection() {
  const [values, setValues] = useState<V11ConfigOverrides>({});
  const [initial, setInitial] = useState<V11ConfigOverrides>({});
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    loadV11ConfigOverrides().then(() => {
      if (cancelled) return;
      const current = getV11ConfigOverrides();
      const seeded: V11ConfigOverrides = {};
      for (const field of V11_CONFIG_FIELDS) {
        seeded[field.key] = (current[field.key] ?? field.default) as number;
      }
      setValues(seeded);
      setInitial(seeded);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);
  const grouped = useMemo(() => {
    const map = new Map<V11ConfigField['group'], V11ConfigField[]>();
    for (const field of V11_CONFIG_FIELDS) {
      const list = map.get(field.group) ?? [];
      list.push(field);
      map.set(field.group, list);
    }
    return Array.from(map.entries());
  }, []);
  const isDirty = useMemo(() => V11_CONFIG_FIELDS.some(f => Number(values[f.key]) !== Number(initial[f.key])), [values, initial]);
  const overrideCount = useMemo(() => V11_CONFIG_FIELDS.filter(f => Number(values[f.key]) !== f.default).length, [values]);
  const update = (key: V11ConfigField['key'], value: number) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleSave = async () => {
    // Only persist values that differ from defaults; the rest fall back to defaults.
    const overrides: V11ConfigOverrides = {};
    for (const f of V11_CONFIG_FIELDS) {
      const v = Number(values[f.key]);
      if (Number.isFinite(v) && v !== f.default) overrides[f.key] = v;
    }
    const applied = await saveV11ConfigOverrides(overrides);
    const seeded: V11ConfigOverrides = {};
    for (const f of V11_CONFIG_FIELDS) {
      seeded[f.key] = (applied[f.key] ?? f.default) as number;
    }
    setValues(seeded);
    setInitial(seeded);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  const handleReset = async () => {
    await resetV11ConfigOverrides();
    const seeded: V11ConfigOverrides = {};
    for (const f of V11_CONFIG_FIELDS) {
      seeded[f.key] = f.default;
    }
    setValues(seeded);
    setInitial(seeded);
  };
  if (loading) {
    return <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-500" data-id="element-6382">
        Laden…
      </div>;
  }
  return <div className="space-y-4" data-id="element-6383">
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-4 sm:px-5 py-4 flex items-start gap-3" data-id="element-6384">
        <Info className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" data-id="element-6385" />
        <div className="flex-1 min-w-0" data-id="element-6386">
          <p className="text-sm font-bold text-indigo-900" data-id="element-6387">
            Adaptive Engine — V11_CONFIG runtime overrides (P-14)
          </p>
          <p className="text-xs sm:text-sm text-indigo-800 mt-1 leading-relaxed" data-id="element-6388">
            Deze instellingen overschrijven de pedagogische parameters van de
            adaptive engine zonder code-wijziging — bedoeld voor A/B testing en
            fine-tuning. Wijzigingen worden direct toegepast op alle volgende
            sessies. Reset zet alles terug naar de defaults.
          </p>
          <p className="text-xs text-indigo-700 mt-2 font-medium" data-id="element-6389">
            {overrideCount === 0 ? 'Alle parameters staan op standaardwaarden.' : `${overrideCount} parameter${overrideCount === 1 ? '' : 's'} overschreven.`}
          </p>
        </div>
      </div>

      {grouped.map(([group, fields]) => {
      const meta = GROUP_META[group];
      return <div key={group} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-6390">
            <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-6391">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-6392">
                {meta.label}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-6393">{meta.description}</p>
            </div>
            <div className="px-4 sm:px-5 divide-y divide-gray-50" data-id="element-6394">
              {fields.map(field => <V11Field key={field.key} field={field} value={Number(values[field.key] ?? field.default)} onChange={v => update(field.key, v)} data-id="element-6395" />)}
            </div>
          </div>;
    })}

      <div className="flex items-center justify-between gap-3 sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 px-4 py-3 -mx-4 sm:mx-0 sm:rounded-xl sm:border sm:shadow-sm" data-id="element-6396">
        <button onClick={handleReset} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-red-600 transition-colors px-3 py-2 rounded-lg hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:outline-none" data-id="element-6397">
          <RotateCcw className="w-4 h-4" data-id="element-6398" />
          Reset naar defaults
        </button>
        <Button onClick={handleSave} disabled={!isDirty} variant={saved ? 'success' : 'primary'} size="sm" className={saved || !isDirty ? '' : 'bg-indigo-600 hover:bg-indigo-700 shadow-none'} data-id="element-6399">
          {saved ? <>
              <Check className="w-4 h-4 mr-1.5" data-id="element-6400" /> Opgeslagen
            </> : 'Toepassen'}
        </Button>
      </div>
    </div>;
}
interface V11FieldProps {
  field: V11ConfigField;
  value: number;
  onChange: (v: number) => void;
}
function V11Field({
  field,
  value,
  onChange
}: V11FieldProps) {
  const isOverride = value !== field.default;
  const step = field.step ?? 1;
  const handleNumber = (raw: string) => {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) onChange(parsed);
  };
  return <div className="flex flex-col sm:flex-row sm:items-center justify-between py-3 gap-3" data-id="element-6401">
      <div className="flex-1 pr-4 min-w-0" data-id="element-6402">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-6403">
          <span className="text-sm font-medium text-gray-900" data-id="element-6404">
            {field.label}
          </span>
          {isOverride && <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700" data-id="element-6405">
              Override
            </span>}
        </div>
        <div className="text-xs text-gray-500 mt-0.5" data-id="element-6406">{field.description}</div>
        <div className="text-[10px] text-gray-400 mt-0.5 font-mono" data-id="element-6407">
          {String(field.key)} · default {field.default} · range {field.min}–
          {field.max}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0" data-id="element-6408">
        <input type="range" min={field.min} max={field.max} step={step} value={value} onChange={e => handleNumber(e.target.value)} className="w-32 sm:w-40 accent-indigo-600" aria-label={field.label} data-id="element-6409" />
        <input type="number" min={field.min} max={field.max} step={step} value={value} onChange={e => handleNumber(e.target.value)} className="w-20 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none font-mono" aria-label={`${field.label} (numeriek)`} data-id="element-6410" />
      </div>
    </div>;
}